/**
 * V4 Position layer for selected vehicle's estimated position.
 * Shows V4 estimated position when a vehicle with GPS is selected and toggle is enabled.
 *
 * PERFORMANCE: Uses specific selector for selected vehicle only, not entire vehicles Map.
 */

import { memo, useMemo } from 'react';
import { Source, Layer } from 'react-map-gl/maplibre';
import type { CircleLayerSpecification, LineLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl';
import type { FeatureCollection, Point, LineString } from 'geojson';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { V4_POSITION_CONFIG } from '../../config';
import { haversineDistance } from '../../utils/haversine';
import { CROSSHAIR_ICON } from '../../utils/vehicleIcons';

const V4_POSITION_SOURCE_ID = 'v4-position-source';
const V4_BACKGROUND_LAYER_ID = 'v4-position-background';
const V4_ICON_LAYER_ID = 'v4-position-icon';
const V4_LINE_SOURCE_ID = 'v4-deviation-source';
const V4_LINE_LAYER_ID = 'v4-deviation-line';

interface V4PositionFeatureProperties {
  id: string;
  color_bg: string;
}

const EMPTY_GEOJSON: FeatureCollection<Point, V4PositionFeatureProperties> = {
  type: 'FeatureCollection',
  features: [],
};

const EMPTY_LINE_GEOJSON: FeatureCollection<LineString> = {
  type: 'FeatureCollection',
  features: [],
};

export const V4PositionLayer = memo(function V4PositionLayer() {
  // Use specific selector - only re-renders when selected vehicle changes
  const selectedVehicle = useVehicleStore((state) =>
    state.selectedVehicleId ? state.vehicles.get(state.selectedVehicleId) : null
  );
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const showSelectedV4Position = useSettingsStore((state) => state.showSelectedV4Position);

  // Convert selected vehicle's V4 position to GeoJSON
  const geojson = useMemo(() => {
    // Early exit if no selection or toggle off
    if (!selectedVehicle || !showSelectedV4Position) {
      return EMPTY_GEOJSON;
    }

    // Must have V1-DA GPS data and V4 position available.
    // Use v1LastUpdateTime (stable) instead of data_source (flips on each update, causing flicker).
    if (!selectedVehicle.v4_position || selectedVehicle.v1LastUpdateTime === null) {
      return EMPTY_GEOJSON;
    }

    // Skip dead vehicles
    if (selectedVehicle.visualState === 'dead') {
      return EMPTY_GEOJSON;
    }

    // Check distance threshold
    const distance = haversineDistance(
      selectedVehicle.targetLatitude,
      selectedVehicle.targetLongitude,
      selectedVehicle.v4_position.latitude,
      selectedVehicle.v4_position.longitude
    );

    // Only show V4 position if deviation exceeds threshold
    if (distance < V4_POSITION_CONFIG.MERGE_THRESHOLD_METERS) {
      return EMPTY_GEOJSON;
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [selectedVehicle.v4_position.longitude, selectedVehicle.v4_position.latitude],
          },
          properties: {
            id: selectedVehicle.service_journey_id,
            color_bg: selectedVehicle.line?.color_bg || '#666666',
          },
        },
      ],
    } as FeatureCollection<Point, V4PositionFeatureProperties>;
  }, [selectedVehicle, showSelectedV4Position]);

  // Deviation line from GPS position to V4 position
  const lineGeojson = useMemo(() => {
    if (!selectedVehicle || !showSelectedV4Position) {
      return EMPTY_LINE_GEOJSON;
    }
    if (!selectedVehicle.v4_position || selectedVehicle.v1LastUpdateTime === null) {
      return EMPTY_LINE_GEOJSON;
    }
    if (selectedVehicle.visualState === 'dead') {
      return EMPTY_LINE_GEOJSON;
    }
    const distance = haversineDistance(
      selectedVehicle.targetLatitude,
      selectedVehicle.targetLongitude,
      selectedVehicle.v4_position.latitude,
      selectedVehicle.v4_position.longitude
    );
    if (distance < V4_POSITION_CONFIG.MERGE_THRESHOLD_METERS) {
      return EMPTY_LINE_GEOJSON;
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [selectedVehicle.targetLongitude, selectedVehicle.targetLatitude],
              [selectedVehicle.v4_position.longitude, selectedVehicle.v4_position.latitude],
            ],
          },
          properties: {},
        },
      ],
    } as FeatureCollection<LineString>;
  }, [selectedVehicle, showSelectedV4Position]);

  // Deviation line style — orange dashed
  const lineLayerStyle: LineLayerSpecification = useMemo(
    () => ({
      id: V4_LINE_LAYER_ID,
      type: 'line',
      source: V4_LINE_SOURCE_ID,
      paint: {
        'line-color': '#f97316', // Orange
        'line-width': 2.5,
        'line-opacity': 0.7,
        'line-dasharray': [4, 3],
      },
    }),
    []
  );

  // V4 position background layer - colored circle with white border (estimated indicator)
  const backgroundLayerStyle: CircleLayerSpecification = useMemo(
    () => ({
      id: V4_BACKGROUND_LAYER_ID,
      type: 'circle',
      source: V4_POSITION_SOURCE_ID,
      paint: {
        'circle-radius': 14, // Same size as main vehicle layer
        'circle-color': ['get', 'color_bg'],
        'circle-stroke-color': '#ffffff', // White border for V4 estimated
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 0.5,
        'circle-opacity': 0.5,
      },
    }),
    []
  );

  // V4 position icon layer - yellow crosshair
  const iconLayerStyle: SymbolLayerSpecification = useMemo(
    () => ({
      id: V4_ICON_LAYER_ID,
      type: 'symbol',
      source: V4_POSITION_SOURCE_ID,
      layout: {
        'icon-image': CROSSHAIR_ICON,
        'icon-size': 2.0,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
      paint: {
        'icon-color': '#FFD700', // Yellow crosshair
        'icon-opacity': 0.85,
      },
    }),
    []
  );

  // V4 marker is visual-only (not clickable) — V1 GPS is the only true position
  // Don't render if toggle is off or no vehicle selected
  if (!showSelectedV4Position || !selectedVehicleId) {
    return null;
  }

  return (
    <>
      {/* Deviation line (rendered below marker) */}
      <Source id={V4_LINE_SOURCE_ID} type="geojson" data={lineGeojson}>
        <Layer {...lineLayerStyle} />
      </Source>

      {/* V4 position marker: background circle + crosshair icon */}
      <Source id={V4_POSITION_SOURCE_ID} type="geojson" data={geojson}>
        <Layer {...backgroundLayerStyle} />
        <Layer {...iconLayerStyle} />
      </Source>
    </>
  );
});
